/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CartLinePriceEditor, ICartLinePriceEditorResources, PriceComponent } from '@msdyn365-commerce/components';
import MsDyn365, {
    IComponentProps,
    IGridSettings,
    IImageSettings,
    Image,
    isChannelTypeB2B,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import {
    AttributeTextValue,
    CartLine,
    ChannelDeliveryOptionConfiguration,
    DiscountLine,
    DiscountLineType,
    ProductCatalog,
    SalesLine,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getFallbackImageUrl, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IncrementalQuantity, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import * as React from 'react';
import { getCustomerPreferencesAsync, updateCustomerPartNumberAsync } from '../../../../actions/DataActionExtension.g';
import { ICustomerPreferences } from '../../../../actions/DataServiceEntities.g';
import useAttributeValue from '../../../../modules/common/useAttributeValue';
import { getExtensionPropertyValue } from '../../../../modules/utilities/Helper';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
export interface ICartlineResourceString {
    /**
     * Display string for discount label.
     */
    discountStringText: string;

    /**
     * String for size key.
     */
    sizeString: string;

    /**
     * String for color key.
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension.
     */
    configString: string;

    /**
     * String for style key.
     */
    styleString: string;

    /**
     * String for amount key.
     */
    amountString?: string;

    /**
     * Display string for quantity label.
     */
    quantityDisplayString: string;

    /**
     * Display string for quantity label.
     */
    inputQuantityAriaLabel: string;

    /**
     * Aria label for the decrement button in quantity component.
     */
    decrementButtonAriaLabel: string;

    /**
     * Aria label for the increment button in quantity component.
     */
    incrementButtonAriaLabel: string;

    /**
     * Original text screenreader.
     */
    originalPriceText: string;

    /**
     * Current text screenreader.
     */
    currentPriceText: string;

    /**
     * Shipping Charges Text.
     */
    shippingChargesText: string;

    /**
     * Price Editor Charges Text.
     */
    priceEditorResources?: ICartLinePriceEditorResources;

    /**
     * Sales agreement price prompt text.
     */
    salesAgreementPricePrompt?: string;

    /**
     * Shipping text.
     */
    shippingText?: string;
}

export interface ProductConversionMatrix {
    PRODUCT: number;
    FACTOR: number;
    FromUnit: string;
    ToUnit: string;
}

/**
 * Represents cart line data interface.
 */
interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
    catalogs?: ProductCatalog[];
}

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /**
     * The flag to change the quantity component from interactible to static.
     */
    disableQuantityInteractivity?: boolean;

    /**
     * The primary image url.
     */
    primaryImageUrl?: string;

    /**
     * The product url.
     */
    productUrl?: string;

    /**
     * GridSettings for the product image in cartLine.
     */
    gridSettings: IGridSettings;

    /**
     * ImageSettings for the product image in cartLine.
     */
    imageSettings: IImageSettings;

    /**
     * Boolean flag to indicate if the item is out of stock.
     */
    isOutOfStock?: boolean;

    /**
     * Flag to make quantity section editable.
     */
    isQuantityEditable?: boolean;

    /**
     * Max quantity for line item.
     */
    maxQuantity?: number;

    /**
     * Current quantity for line item.
     */
    currentQuantity?: number;

    /**
     * Resource string for the component.
     */
    resources: ICartlineResourceString;

    /**
     * SalesLine flag.
     */
    isSalesLine?: boolean;

    /**
     * Error message to show in place of quantity.
     */
    errorMessage?: string;

    /**
     * Inventory information label.
     */
    inventoryInformationLabel?: string;

    /**
     * Inventory information class name.
     */
    inventoryLabelClassName?: string;

    /**
     * Flag to show/hide shipping charges for line items.
     */
    showShippingChargesForLineItems?: boolean;

    /**
     * Boolean flag to indicate if cart state status is ready.
     */
    isCartStateReady?: boolean;

    /**
     * Chanel Delivery Option configuration is from api.
     */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;

    /**
     * The cart line delivery location.
     */
    deliveryLocation?: string;

    /**
     * Quantity onChange callback.
     */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): boolean;
    priceCurrency?: string;
}

// /**
//  * Represents dimensions strings.
//  */
// interface IDimensionStrings {
//     /**
//      * String for size key.
//      */
//     sizeString: string;

//     /**
//      * String for color key.
//      */
//     colorString: string;

//     /**
//      * String associated with the configuration product dimension.
//      */
//     configString: string;

//     /**
//      * String for style key.
//      */
//     styleString: string;

//     /**
//      * String for amount key.
//      */
//     amountString?: string;
// }

/**
 * Renders catalog label for the cart line.
 * @param props - Cart line props.
 * @returns Catalog label.
 */
const CatalogLabelComponent: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const catalogId = props.data.cartLine.CatalogId;
    validateCatalogId(catalogId);

    if (!isChannelTypeB2B(props.context.actionContext.requestContext) || !catalogId || !ArrayExtensions.hasElements(props.data.catalogs)) {
        return null;
    }

    const catalog = props.data.catalogs.find(item => item.RecordId === catalogId);

    if (!catalog || !catalog.Name) {
        return null;
    }

    return <div className='msc-cart-line__catalog-label'>{catalog.Name}</div>;
};

const cartLineItemFunctions = {
    /**
     * Render product dimensions.
     * @param product - Product data.
     * @param dimensionStrings - Dimension strings interface.
     * @returns JSX Element.
     */
    // renderProductDimensions: (product: SimpleProduct, dimensionStrings: IDimensionStrings) => {
    //     if (!product.Dimensions) {
    //         return [];
    //     }

    //     return product.Dimensions.map(productDimension => {
    //         if (productDimension.DimensionTypeValue === ProductDimensionType.Color) {
    //             return (
    //                 <div key={`${product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
    //                     <span className='msc-cart-line__product-variant-color'>
    //                         {'Length'}
    //                         {': '}
    //                         <span className='name'>{productDimension.DimensionValue?.Value?.split('.')[0]}</span>
    //                     </span>
    //                 </div>
    //             );
    //         }

    //         if (productDimension.DimensionTypeValue === ProductDimensionType.Configuration) {
    //             return (
    //                 <div key={`${product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
    //                     <span className='msc-cart-line__product-configuration'>
    //                         {dimensionStrings.configString}
    //                         {': '}
    //                         <span className='name'>{productDimension.DimensionValue?.Value}</span>
    //                     </span>
    //                 </div>
    //             );
    //         }

    //         if (productDimension.DimensionTypeValue === ProductDimensionType.Size) {
    //             return (
    //                 <div key={`${product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
    //                     <span className='msc-cart-line__product-variant-size'>
    //                         {'Width'}
    //                         {': '}
    //                         <span className='name'>{productDimension.DimensionValue?.Value?.split('.')[0]}</span>
    //                     </span>
    //                 </div>
    //             );
    //         }

    //         if (productDimension.DimensionTypeValue === ProductDimensionType.Style) {
    //             return (
    //                 <div key={`${product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
    //                     <span className='msc-cart-line__product-variant-style'>
    //                         {product.IsGiftCard ? dimensionStrings.amountString : dimensionStrings.styleString}
    //                         {': '}
    //                         <span className='name'>{productDimension.DimensionValue?.Value}</span>
    //                     </span>
    //                 </div>
    //             );
    //         }
    //         return null;
    //     });
    // },

    renderProductWeightTotalLFSF: (quantity: number, totalWeight: string | undefined, conversionMatrix: string | undefined) => {
        let conversionValues: ProductConversionMatrix[] = [];

        if (conversionMatrix) {
            conversionValues = JSON.parse(conversionMatrix);
        }
        var lfsfValue = '';
        let lengthUom: string | undefined = 'ft';
        if (conversionValues.length > 0) {
            var productUnit = 'pc';
            var availableProductUoms = conversionValues.map(x => {
                return x.ToUnit;
            });
            if (availableProductUoms.indexOf('sqin') >= 0) {
                lengthUom = 'sqin';
            }
            if (availableProductUoms.indexOf('sqft') >= 0) {
                lengthUom = 'sqft';
            }
            var conversionValue = conversionValues.find(cv => {
                if (cv.FromUnit === productUnit && lengthUom == 'sqft' && cv.ToUnit === 'sqin') {
                    return true;
                }
                return cv.FromUnit === productUnit && cv.ToUnit === lengthUom;
            });
            if (conversionValue) {
                var unitValue = quantity > 1 ? quantity * conversionValue.FACTOR : conversionValue.FACTOR;
                if (conversionValue.ToUnit === 'sqin') {
                    unitValue = unitValue * 0.00694444; //1 sqin = 0.00694444  sqft
                }
                lfsfValue = formatNumber(unitValue);
            }
        }

        return (
            <>
                {!!totalWeight && (
                    <div className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {'Total Weight'}
                            {': '}
                            <span className='name'>{totalWeight}</span>
                        </span>
                    </div>
                )}

                {!!lfsfValue && (
                    <div className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {'Total LF/SF'}
                            {': '}
                            <span className='name'>{lfsfValue}</span>
                        </span>
                    </div>
                )}
            </>
        );
    },

    /**
     * Gets the react node for product unit of price display.
     * @param props - The cart line props.
     * @returns The node representing markup for unit of measure component.
     */
    renderProductUnitPrice: (props: ICartLineProps) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per PC:'}
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        );
    },

    renderProductPricePerPound: (props: ICartLineProps, productWeight: number) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per LB:'}
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price / productWeight
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price / productWeight
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        );
    },

    renderProductPricePerFTSQFT: (props: ICartLineProps, productLFSF: number) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per FT / SQFT:'}
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price / productLFSF
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price / productLFSF
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        );
    },

    renderProductPricePerINSQIN: (props: ICartLineProps, length: number, width: number) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        const pricePerInSqIn = props.data.cartLine.Price / (length * (width === 0 ? 1 : width)); // 1 sqft = 144 sqin
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per IN / SQIN:'}
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: pricePerInSqIn
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: pricePerInSqIn
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        );
    },

    renderCustomerPartNumberNode: (
        props: ICartLineProps,
        cpnEnabled: boolean | undefined,
        cpn: string | undefined,
        cpnEditAllowed?: boolean
    ) => {
        const [textBoxValue, setTextBoxValue] = React.useState<string>('');

        React.useEffect(() => {
            if (cpnEnabled && cpn) {
                setTextBoxValue(cpn);
            }
        }, [props, cpnEnabled]);
        return cpnEnabled && cpn !== undefined && cpn !== '' ? (
            <tr className='form-group delivery-note'>
                <td style={{ width: '30%' }}>
                    <span>Customer Part #: </span>
                </td>
                <td style={{ width: '70%' }} className='customer-po-controls'>
                    {props.isQuantityEditable ? (
                        <input
                            type='text'
                            className='form-control'
                            maxLength={props.context.app.config.setNoteLength} // Set character limit
                            disabled={!cpnEditAllowed}
                            onChange={e => {
                                setTextBoxValue(e.target.value);
                            }}
                            onBlur={async e => {
                                const attributeValues = props.data.cartLine.AttributeValues;
                                if (
                                    attributeValues &&
                                    attributeValues?.find(av => {
                                        return av.Name === 'CustomerPartNumber';
                                    })
                                ) {
                                    attributeValues?.forEach(attr => {
                                        const attrText = attr as AttributeTextValue;
                                        if (attr.Name === 'CustomerPartNumber') {
                                            attrText.TextValue = textBoxValue;
                                        }
                                    });
                                } else {
                                    attributeValues?.push({
                                        // @ts-expect-error -- Need to provide data type.
                                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                                        Name: 'CustomerPartNumber',
                                        TextValue: textBoxValue,
                                        ExtensionProperties: [],
                                        TextValueTranslations: []
                                    });
                                }
                                props.data.cartLine.AttributeValues = attributeValues;
                                props.data.cartState?.updateCartLines({
                                    updatedCartLinesObject: [props.data.cartLine]
                                });
                                await updateCustomerPartNumberAsync(
                                    { callerContext: props.context.actionContext },
                                    props.data.cartState!.cart.CustomerId!,
                                    props.data.cartLine.ItemId!,
                                    props.data.cartLine!.InventoryDimensionId!,
                                    textBoxValue
                                );
                            }}
                            value={textBoxValue}
                        />
                    ) : (
                        <>{(textBoxValue !== undefined || textBoxValue !== '') && <span>{textBoxValue} </span>}</>
                    )}
                </td>
            </tr>
        ) : (
            <></>
        );
    },

    renderCustomerPONode: (props: ICartLineProps, customerPOEnabled: boolean | undefined, customerPO: string | undefined) => {
        const [textBoxValue, setTextBoxValue] = React.useState<string>('');
        React.useEffect(() => {
            if (customerPOEnabled && customerPO) {
                setTextBoxValue(customerPO);
            }
        }, [props, customerPOEnabled]);
        return customerPOEnabled ? (
            props.data.cartState && !!props.isQuantityEditable ? (
                <tr className='form-group customer-po'>
                    <td style={{ width: '30%' }}>
                        <span>Customer PO: </span>
                    </td>
                    <td style={{ width: '70%' }} className='customer-po-controls'>
                        <input
                            type='text'
                            className='form-control'
                            onChange={e => {
                                setTextBoxValue(e.target.value);
                            }}
                            onBlur={e => {
                                const attributeValues = props.data.cartLine.AttributeValues;
                                if (
                                    attributeValues &&
                                    attributeValues?.find(av => {
                                        return av.Name === 'CustomerPOLine';
                                    })
                                ) {
                                    attributeValues?.forEach(attr => {
                                        const attrText = attr as AttributeTextValue;
                                        if (attr.Name === 'CustomerPOLine') {
                                            attrText.TextValue = textBoxValue;
                                        }
                                    });
                                } else {
                                    attributeValues?.push({
                                        // @ts-expect-error -- Need to provide data type.
                                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                                        Name: 'CustomerPOLine',
                                        TextValue: textBoxValue,
                                        ExtensionProperties: [],
                                        TextValueTranslations: []
                                    });
                                }
                                props.data.cartLine.AttributeValues = attributeValues;
                                props.data.cartState?.updateCartLines({
                                    updatedCartLinesObject: [props.data.cartLine]
                                });
                            }}
                            value={textBoxValue}
                        />
                    </td>
                </tr>
            ) : (
                (textBoxValue !== undefined || textBoxValue !== '') && (
                    <>
                        <div className='form-group'>
                            <span>Customer PO: {textBoxValue} </span>
                        </div>
                    </>
                )
            )
        ) : (
            <></>
        );
    },

    renderDeliveryNote: (props: ICartLineProps, deliveryNoteEnabled: boolean | undefined, deliveryNote: string | undefined) => {
        const [textBoxValue, setTextBoxValue] = React.useState<string>('');
        React.useEffect(() => {
            if (deliveryNoteEnabled && deliveryNote) {
                setTextBoxValue(deliveryNote);
            }
        }, [props, deliveryNoteEnabled]);
        return deliveryNoteEnabled && props.data.cartState ? (
            <tr className='form-group delivery-note'>
                <td style={{ width: '30%' }}>
                    <span>Note: </span>
                </td>
                <td style={{ width: '70%' }} className='customer-po-controls'>
                    {props.isQuantityEditable ? (
                        <textarea
                            className='form-control'
                            maxLength={props.context.app.config.setNoteLength} // Set character limit
                            onChange={e => {
                                setTextBoxValue(e.target.value);
                            }}
                            onBlur={e => {
                                const attributeValues = props.data.cartLine.AttributeValues;
                                if (
                                    attributeValues &&
                                    attributeValues?.find(av => {
                                        return av.Name === 'LineLevelNotes';
                                    })
                                ) {
                                    attributeValues?.forEach(attr => {
                                        const attrText = attr as AttributeTextValue;
                                        if (attr.Name === 'LineLevelNotes') {
                                            attrText.TextValue = textBoxValue;
                                        }
                                    });
                                } else {
                                    attributeValues?.push({
                                        // @ts-expect-error -- Need to provide data type.
                                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                                        Name: 'LineLevelNotes',
                                        TextValue: textBoxValue,
                                        ExtensionProperties: [],
                                        TextValueTranslations: []
                                    });
                                }
                                props.data.cartLine.AttributeValues = attributeValues;
                                props.data.cartState?.updateCartLines({
                                    updatedCartLinesObject: [props.data.cartLine]
                                });
                            }}
                            value={textBoxValue}
                        />
                    ) : (
                        <>{(textBoxValue !== undefined || textBoxValue !== '') && <span>{textBoxValue} </span>}</>
                    )}
                </td>
            </tr>
        ) : (
            <></>
        );
    },

    renderProductUnitPriceCWT: (
        props: ICartLineProps,
        CWTPrice: string | undefined,
        priceUom: string | undefined,
        quantity: number | undefined,
        conversionMatrix: string | undefined,
        price: number | undefined
    ) => {
        let pricePerFt: number = 0;
        if (priceUom?.toLowerCase() === 'cwt' && !!!CWTPrice) {
            return null;
        }
        if (priceUom?.toLowerCase() === 'ft') {
            let conversionValues: ProductConversionMatrix[] = [];

            if (conversionMatrix) {
                conversionValues = JSON.parse(conversionMatrix);
            }
            let lengthUom: string | undefined = 'ft';
            if (conversionValues.length > 0) {
                var productUnit = 'pc';
                var availableProductUoms = conversionValues.map(x => {
                    return x.ToUnit;
                });
                if (availableProductUoms.indexOf('sqin') >= 0) {
                    lengthUom = 'sqin';
                }
                if (availableProductUoms.indexOf('sqft') >= 0) {
                    lengthUom = 'sqft';
                }
                var conversionValue = conversionValues.find(cv => {
                    if (cv.FromUnit === productUnit && lengthUom == 'sqft' && cv.ToUnit === 'sqin') {
                        return true;
                    }
                    return cv.FromUnit === productUnit && cv.ToUnit === lengthUom;
                });
                if (conversionValue) {
                    var unitValue = quantity! > 1 ? quantity! * conversionValue.FACTOR : conversionValue.FACTOR;
                    if (conversionValue.ToUnit === 'sqin') {
                        unitValue = unitValue * 0.00694444; //1 sqin = 0.00694444  sqft
                    }
                    pricePerFt = parseFloat((price! / unitValue).toFixed(2));
                }
            }
        }
        return priceUom === 'cwt' ? (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per CWT:'}
                    <PriceComponent
                        data={{
                            price: {
                                CustomerContextualPrice: parseFloat(CWTPrice!)
                            }
                        }}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        ) : pricePerFt > 0 ? (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    {' '}
                    {'Price per Ft:'}
                    <PriceComponent
                        data={{
                            price: {
                                CustomerContextualPrice: pricePerFt
                            }
                        }}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        currencyCode={props.priceCurrency}
                    />
                </span>
            </div>
        ) : (
            <></>
        );
    },

    /**
     * Render discount lines.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || !ArrayExtensions.hasElements(props.data.cartLine.DiscountLines)) {
            return null;
        }

        /**
         * Get the updated discount percentage.
         * @param discountLine - The discount line.
         * @param cartLine - The cart line.
         * @returns The updated discount percentage.
         */
        const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
            const numberZero = 0;
            const decimalPrecision = 2;
            const fullPercentage = 100;
            let updatedPercent: number = discountLine.Percentage ?? numberZero;

            if (
                (discountLine.Percentage === numberZero ||
                    (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
                cartLine.Price &&
                cartLine.Price > numberZero &&
                cartLine.Quantity &&
                cartLine.Quantity !== numberZero
            ) {
                const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;
                updatedPercent = Number(
                    ((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision)
                );
            }

            return updatedPercent;
        };

        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId ?? index} className='msc-cart-line__product-discount'>
                    <span>{discountLine.OfferName ? discountLine.OfferName : ''}</span>
                    <span className='msc-cart-line-item-product-discount-price'>
                        <PriceComponent
                            data={
                                props.isSalesLine
                                    ? {
                                          price: {
                                              // @ts-expect-error -- Auto suppressed.
                                              CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: discountLine.EffectiveAmount,
                                              BasePrice: discountLine.EffectiveAmount
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            currencyCode={props.priceCurrency}
                        />
                    </span>
                    <span className='msc-cart-line__product-discount-percentage'>
                        {` (${discountLine.Percentage !== undefined ? discountPercent(discountLine, props.data.cartLine) : ''}%)`}
                    </span>
                </div>
            );
        });
    },

    /**
     * Render inventory label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderInventoryLabel: (props: ICartLineProps) => {
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';

        return <p className={inventoryCssName}>{props.inventoryInformationLabel}</p>;
    },

    /**
     * Render shipping label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!(
                cartline.DeliveryMode &&
                channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
            );
        } else {
            hasShippingMethod = !!(cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode);
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines ?? []).filter(chargeLine => chargeLine.IsShipping);

        if (!ArrayExtensions.hasElements(shippingChargeLines)) {
            return undefined;
        }

        const defaultCharge: number = 0;
        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount ?? defaultCharge);
        }, defaultCharge);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText='Free'
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
                currencyCode={props.priceCurrency}
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },

    /**
     * Render other charges.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines?.filter(chargeline => !chargeline.IsShipping);

        return (
            otherCharges?.map((otherCharge, index) => {
                const itemIndex = index;
                return otherCharge.CalculatedAmount ? (
                    <div className='msc-cart-line__other-charges' key={itemIndex}>
                        <label className='msc-cart-line__other-charges-label'>{`${String(otherCharge.Description)}:`}</label>
                        <span className='other-charge-value'>
                            <PriceComponent
                                data={{
                                    price: {
                                        CustomerContextualPrice: otherCharge.CalculatedAmount
                                    }
                                }}
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                className='msc-cart-line__other-charges-actual'
                                currencyCode={props.priceCurrency}
                            />
                        </span>
                    </div>
                ) : (
                    ''
                );
            }) ?? undefined
        );
    },

    getExtensionPropertyValue: (cartLine: CartLine | undefined, propertyName: string): boolean | number | string | undefined => {
        let propertyValue: boolean | number | string | undefined = '';
        if (cartLine) {
            if (cartLine.ExtensionProperties) {
                var extension = cartLine.ExtensionProperties.find(ext => {
                    return ext.Key === propertyName;
                });
                if (extension) {
                    propertyValue = extension?.Value?.StringValue || extension?.Value?.BooleanValue || extension.Value?.DecimalValue;
                }
            }
        }
        return propertyValue;
    },

    /**
     * Render the sales agreement prompt.
     * @param props - The ICartLineProps.
     * @returns The JSX.Element.
     */
    renderSalesAgreementPrompt: (props: ICartLineProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- We use 0 to indicate no sales agreement applied on the cart line.
        if (props.data.cartLine.SalesAgreementLineRecordId === 0) {
            return null;
        }
        return <div className='msc-cart-line__sales-agreement-prompt'>{props.resources.salesAgreementPricePrompt}</div>;
    }
};

/**
 * Render cartline component.
 * @param props - The cart line props.
 * @returns JSX element.
 */
// eslint-disable-next-line no-redeclare
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, resources } = props;
    const { product, cartLine } = props.data;
    const [CWTPrice, setCWTPrice] = React.useState<string>();
    const [ConversionMatrix, setConversionMatrix] = React.useState<string>();
    const [priceUOM, setPriceUOM] = React.useState<string>();
    const [cpnEnabled, setCPNEnabled] = React.useState<boolean>();
    const customerPartNumerEditAllowed = useAttributeValue(cartLine, 'OBSAllowEditCustPartStoreFront');
    const deliverNoteEnabled = props.context.app.config.enableLineLevelDeliveryNotes;
    const isCustomProduct = cartLineItemFunctions.getExtensionPropertyValue(cartLine, 'IsCustomProduct');
    const productLength = useAttributeValue(cartLine, 'CustomLength');
    const productWidth = useAttributeValue(cartLine, 'CustomWidth');
    const productWeight = useAttributeValue(cartLine, 'CustomWeight');
    const productLFSF = useAttributeValue(cartLine, 'CustomLFSF');
    const deliveryNote = useAttributeValue(cartLine, 'LineLevelNotes');
    const cpn = useAttributeValue(cartLine, 'CustomerPartNumber');
    const poNumber = useAttributeValue(cartLine, 'CustomerPOLine');
    const [customerPOEnabled, setCustomerPOEnabled] = React.useState<boolean>();
    const [customerPreferences, setCustomerPreferences] = React.useState<ICustomerPreferences>();
    const leadTime = useAttributeValue(cartLine, 'LeadTimeLine');

    React.useEffect(() => {
        async function fetchCustomerPreferences() {
            if (props.context.request.user.isAuthenticated) {
                const preferences = await getCustomerPreferencesAsync(
                    { callerContext: props.context.actionContext },
                    props.context.request.user.customerAccountNumber!
                );
                setCustomerPreferences(preferences);
            }
        }
        fetchCustomerPreferences();
    }, [props.context.request.user.isAuthenticated, props.context.actionContext]);

    React.useEffect(() => {
        if (props.data.cartLine) {
            setCWTPrice(cartLineItemFunctions.getExtensionPropertyValue(cartLine, 'CWTPrice')?.toString());
            setConversionMatrix(cartLineItemFunctions.getExtensionPropertyValue(cartLine, 'ConversionMatrix')?.toString());
            setPriceUOM(cartLineItemFunctions.getExtensionPropertyValue(cartLine, 'CMAPriceUOM')?.toString());
            if (props.data.cartLine.ItemId && props.data.cartLine.ProductId) {
                setCPNEnabled(
                    props.context.app.config.enableCustomerPartNumber && props.context.request.user.isAuthenticated && !isCustomProduct
                );
                setCustomerPOEnabled(props.context.app.config.enableCustomerPO);
            }
        }
    }, [props.data.cartLine]);

    const fallbackImageUrl =
        product &&
        getFallbackImageUrl(
            product.ItemId,
            props.context.actionContext.requestContext.apiSettings,
            props.context.request.app?.config?.OmniChannelMedia
        );
    //const productDimensions = product && cartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    // const renderProductTotalWeightLFSF = cartLineItemFunctions.renderProductWeightTotalLFSF(
    //     props.data.cartLine.Quantity!,
    //     TotalWeight,
    //     ConversionMatrix
    // );
    const renderProductUnitPrice = cartLineItemFunctions.renderProductUnitPrice(props);
    const renderProductPricePerPound = cartLineItemFunctions.renderProductPricePerPound(props, +productWeight!);
    const renderProductPricePerFTSQFT = cartLineItemFunctions.renderProductPricePerFTSQFT(props, +productLFSF!);
    const renderProductPricePerINSQIN = cartLineItemFunctions.renderProductPricePerINSQIN(props, +productLength!, +productWidth!);
    const renderProductUnitPriceCWT = cartLineItemFunctions.renderProductUnitPriceCWT(
        props,
        CWTPrice,
        priceUOM,
        props.data.cartLine.Quantity!,
        ConversionMatrix,
        props.data.cartLine.Price! * props.data.cartLine.Quantity!
    );
    const renderCustomerPartNumber = cartLineItemFunctions.renderCustomerPartNumberNode(
        props,
        cpnEnabled,
        cpn?.toString(),
        customerPartNumerEditAllowed === '1'
    );
    const renderCustomerPurchaseOrder = cartLineItemFunctions.renderCustomerPONode(props, customerPOEnabled, poNumber?.toString());
    const renderDeliveryNote = cartLineItemFunctions.renderDeliveryNote(props, deliverNoteEnabled, deliveryNote?.toString());
    const imageSettings = props.imageSettings;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const renderDiscountLines = cartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = cartLineItemFunctions.renderInventoryLabel(props);
    const renderShippingLabel = cartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = cartLineItemFunctions.renderOtherCharges(props);
    const renderSalesAgreementPrompt = cartLineItemFunctions.renderSalesAgreementPrompt(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const productAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name;

    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';
    const cartLineContentClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-content' : 'msc-cart-line__content';

    /**
     * Render other charges.
     * @param newValue - The cart line props.
     * @returns Boolean.
     */
    const onChange = (newValue: number): boolean => {
        if (props.quantityOnChange) {
            return props.quantityOnChange(props.data.cartLine, newValue);
        }
        return true;
    };

    /**
     * Generate error message.
     * @returns JSX element.
     */
    const generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    /**
     * Render Saving component.
     * @returns JSX element.
     */
    const renderSaving = (): JSX.Element | null => {
        if (!props.data.cartLine.DiscountAmount) {
            return null;
        }
        const discountAmountZero: number = 0;

        if (props.data.cartLine.DiscountAmount > discountAmountZero) {
            return (
                <>
                    <span className='msc-cart-line__product-savings-label'>{`${props.resources.discountStringText || 'Discount'}`}</span>
                    <PriceComponent
                        data={
                            props.isSalesLine
                                ? {
                                      price: {
                                          // @ts-expect-error -- Auto suppressed.
                                          CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.DiscountAmount,
                                          BasePrice: props.data.cartLine.DiscountAmount
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='msc-cart-line__product-savings-text'
                        currencyCode={props.priceCurrency}
                    />
                </>
            );
        }
        return null;
    };

    /**
     * Generate quantity and price component.
     * @returns JSX element.
     */
    const generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];
        const defaultMaxQuantity: number = 10;
        const singleQuantity: number = 1;
        //const unitOfMeasure = renderUnitOfMeasure();
        const qtyStep = getExtensionPropertyValue(props.data.cartLine, 'QuantityStep') as number | undefined;
        // No quantity selector for invoice line
        if (!props.data.cartLine.IsInvoiceLine) {
            if (props.data.product && props.isQuantityEditable) {
                nodes.push(
                    <div className='msc-cart-line__product-quantity'>
                        <div className='msc-cart-line__product-quantity-label'>{resources.quantityDisplayString}</div>
                        {generateErrorMessage()}
                        {qtyStep && qtyStep > 1 ? (
                            <span className='quantity-value'>
                                {qtyStep && qtyStep > 1 ? props.data.cartLine.Quantity! * qtyStep : props.data.cartLine.Quantity}{' '}
                                {qtyStep && qtyStep > 1 && props.data.cartLine.UnitOfMeasureSymbol}
                            </span>
                        ) : (
                            <IncrementalQuantity
                                id={`msc-cart-line__quantity_${props.data.product.RecordId}-
                            ${String(props.data.cartLine.DeliveryMode)}-${String(props.data.cartLine.LineId)}`}
                                max={props.maxQuantity ?? defaultMaxQuantity}
                                currentCount={props.currentQuantity}
                                onChange={onChange}
                                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                                key={props.data.cartLine.LineId}
                                disabled={!props.isCartStateReady}
                                isGiftCard={props.data.product.IsGiftCard ?? props.isOutOfStock}
                                telemetryContent={props.telemetryContent}
                            />
                        )}
                    </div>
                );
            } else {
                nodes.push(
                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': props.data.cartLine.Quantity === singleQuantity
                        })}
                    >
                        <label className='quantity-label' htmlFor='quantity'>
                            {resources.quantityDisplayString}
                            {': '}
                        </label>

                        {generateErrorMessage()}

                        <div className='msc-cart-line__quantity-info-list'>
                            <div className='quantity-info-item'>
                                {qtyStep && qtyStep > 1 ? props.data.cartLine.Quantity! * qtyStep : props.data.cartLine.Quantity}{' '}
                                {(qtyStep && qtyStep > 1 ? props.data.cartLine.Quantity! * qtyStep : props.data.cartLine.Quantity) === 1
                                    ? 'piece'
                                    : 'pieces'}
                            </div>
                            {productWeight !== undefined && productWeight !== '' && (
                                <div className='quantity-info-item'>
                                    {formatNumber(+productWeight! * props.data.cartLine.Quantity!)} lbs
                                </div>
                            )}
                            {productLFSF !== undefined && productLFSF !== '' && (
                                <div className='quantity-info-item'>
                                    {formatNumber(+productLFSF! * props.data.cartLine.Quantity!)} ft/sqft
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }
        if (!props.isQuantityEditable) {
            nodes.push(
                <div className='msc-cart-line__product-price'>
                    {customerPreferences?.ShowPricePerPc && renderProductUnitPrice}
                    {customerPreferences?.ShowPricePerCWT && renderProductUnitPriceCWT}
                    {customerPreferences?.ShowPricePerLb && renderProductPricePerPound}
                    {customerPreferences?.ShowPricePerFTSQFT && renderProductPricePerFTSQFT}
                    {customerPreferences?.ShowPricePerINSQIN && renderProductPricePerINSQIN}
                </div>
            );
            nodes.push(
                props.context.request.user.isAuthenticated ? (
                    <div className='msc-cart-line__product-savings'>
                        <PriceComponent
                            data={
                                isSalesLine
                                    ? {
                                          price: {
                                              CustomerContextualPrice: (props.data.cartLine as SalesLine).NetAmountWithAllInclusiveTax,
                                              BasePrice: (props.data.cartLine as SalesLine).GrossAmount
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: (props.data.cartLine as CartLine).ExtendedPrice,
                                              BasePrice: props.data.cartLine.NetPrice
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='msc-cart-line__product-savings-actual'
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            currencyCode={props.priceCurrency}
                        />
                        {renderSaving()}
                        {props.isOutOfStock ? renderInventoryLabel : null}
                    </div>
                ) : (
                    <></>
                )
            );
        }

        return nodes;
    };

    const _renderStoreLocation = (): JSX.Element | null => {
        if (cartLine && cartLine.FulfillmentStoreId) {
            return (
                <div className='msc-cart-line__content__bopis-method'>
                    <span className='pick-up'>{props.deliveryLocation}</span>
                </div>
            );
        } else {
            return (
                <div className='msc-cart-line__content__bopis-method'>
                    <span className='shipping'>{props.deliveryLocation}</span>
                </div>
            );
        }
    };

    return (
        <div className='msc-cart-line'>
            <div className={cartLineContentClassName}>
                <div className='msc-cart-line__product'>
                    {MsDyn365.isBrowser ? (
                        <a className={imgeClassName} href={'#'} aria-label={productName} role='link'>
                            <Image
                                requestContext={props.context.actionContext.requestContext}
                                aria-hidden='true'
                                src={props.primaryImageUrl ?? 'empty'}
                                fallBackSrc={fallbackImageUrl}
                                altText={productName}
                                gridSettings={props.gridSettings}
                                imageSettings={imageSettings}
                                loadFailureBehavior='empty'
                            />
                        </a>
                    ) : null}
                    <CatalogLabelComponent {...props} />
                    <div className='msc-cart-line__product-information'>
                        {MsDyn365.isBrowser ? (
                            <a className='msc-cart-line__product-title' href={'#'} {...productAttribute}>
                                {productName}
                            </a>
                        ) : null}
                        <table className='msc-cart-line__product-variant'>
                            {cpnEnabled && renderCustomerPartNumber}
                            {customerPOEnabled && renderCustomerPurchaseOrder}
                            {deliverNoteEnabled && renderDeliveryNote}
                            {leadTime !== undefined && leadTime !== '' && (
                                <tr className='form-group customer-po'>
                                    <td style={{ width: '30%' }}>
                                        <span>Lead time: </span>
                                    </td>
                                    <td style={{ width: '70%' }} className='customer-po-controls'>
                                        <span>{leadTime}</span>
                                    </td>
                                </tr>
                            )}
                        </table>
                    </div>
                    {/* {renderInventoryLabel} */}
                    {renderDiscountLines}
                    {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                    {renderOtherCharges}
                    {renderSalesAgreementPrompt}
                </div>
                <div className='msc-cart-line__product-attributes'>
                    {productWeight !== undefined && productWeight !== '' && (
                        <div className='msc-cart-line__product-variant-item'>
                            <span className='msc-cart-line__product-variant-color'>
                                {'Total weight'}
                                {': '}
                                <span className='name'>{formatNumber(+productWeight! * props.data.cartLine.Quantity!)}</span>
                                {' lbs'}
                            </span>
                        </div>
                    )}
                    {productLFSF !== undefined && productLFSF !== '' && (
                        <div className='msc-cart-line__product-variant-item'>
                            <span className='msc-cart-line__product-variant-color'>
                                {'Total FT / SQFT'}
                                {': '}
                                <span className='name'>{formatNumber(+productLFSF! * props.data.cartLine.Quantity!)}</span>
                            </span>
                        </div>
                    )}
                </div>
                {generateQuantityAndPrice()}
                {renderSalesAgreementPrompt}
                {props.data.cartLine.IsInvoiceLine && props.data.cartState && props.resources.priceEditorResources && (
                    <CartLinePriceEditor
                        className='msc-cart-line__price-editor-container'
                        context={props.context}
                        resources={props.resources.priceEditorResources}
                        cartState={props.data.cartState}
                        cartLine={props.data.cartLine}
                    />
                )}
                {_renderStoreLocation()}
            </div>
        </div>
    );
};

// @ts-expect-error
export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponentOverride<ICartline>(
    'CartLineItem',
    {
        component: CartLine,
        ...cartLineItemFunctions
    }
);

export default CartLineItemComponent;
function formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { style: 'decimal' }).format(value);
}
